type RouteChangeCallback = (route: string) => void
type HistoryMethod = 'pushState' | 'replaceState'
// pushState and replaceState share one signature; naming it once avoids spreading a union of tuples.
type HistoryStateArgs = Parameters<History['pushState']>

/** Pathname plus query string. The hash is left out so anchor links do not count as navigation. */
const currentRoute = (): string => `${window.location.pathname}${window.location.search}`

/**
 * Calls back when a single-page app moves to another route: after pushState/replaceState and on
 * popstate (back/forward). Replaces @analytics/router-utils, which compared the pathname only and
 * therefore missed query-string routes such as `?tab=2`. The check runs on the next tick so the app
 * has rendered the new view (and its title) before the callback fires. Returns an unsubscribe.
 */
export const onRouteChange = (callback: RouteChangeCallback): (() => void) => {
  let previous = currentRoute()
  let disposed = false

  const check = (): void => {
    if (disposed) return
    const next = currentRoute()
    if (next === previous) return
    previous = next
    callback(next)
  }

  const scheduleCheck = (): void => {
    setTimeout(check, 0)
  }

  const wrap = (method: HistoryMethod): (() => void) => {
    const original = window.history[method].bind(window.history)
    window.history[method] = (...args: HistoryStateArgs): void => {
      original(...args)
      scheduleCheck()
    }
    return () => {
      window.history[method] = original
    }
  }

  const restorePushState = wrap('pushState')
  const restoreReplaceState = wrap('replaceState')
  window.addEventListener('popstate', scheduleCheck)

  return () => {
    disposed = true
    restorePushState()
    restoreReplaceState()
    window.removeEventListener('popstate', scheduleCheck)
  }
}
